import { Fragment, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { ahuBoardApi, useGetAhuUnitQuery, usePatchAhuTunnelMutation } from 'app/services/ahuBoardApi';
import { useMoveAhuSectionMutation } from 'app/services/ahuBoardFormsApi';
import { selectSection } from 'app/slices/ahuBoardSlice';
import type { IAhuBoardSection, IAhuBoardTunnel } from 'models';
import { ahuBoardBus } from '../../bus/ahuBoardBus';
import { airflowFlipPatch } from '../hybridXOffset';
import { refetchHybridLugPlacements } from '../../boardActions';
import { getVisibleTunnelIndexes } from '../../addressing';
import { formatDecimal } from '../../forms/formUtils';
import { validateAll3D, validateTunnel } from '../../validation/validationController';

const MAX_VISIBLE_TUNNEL_INDEXES = 5;

/**
 * Phase 6e1 — Configure Tunnel window (§8.2, tunnels.section.js +
 * tunnels.section.tmpl + sections.order.list.tmpl, markup faithful): tunnel
 * pills, per-tunnel Airflow Direction toggles (the PATCH wire exists from 6d
 * — patchAhuTunnel with `{wait:true}` semantics), drag-reorder section rows
 * ("Name : tag | length" + grip) → `section.move({next_to})` + optimistic
 * renumber + `sections:reordered`.
 *
 * Pills/rows follow external section creation/selection
 * (`storage:section.model:create` / `select:section` — store-driven here).
 *
 * Row reordering is a hand-rolled pointer drag rather than HTML5 drag-and-drop,
 * matching `jquery/draggable.js` (`$.fn.draggables`, bound by tunnels.section.js
 * on `.drag`): the browser's native drag paints a free-floating ghost that
 * follows the cursor in both axes and escapes the panel, which dev never did.
 * Here the row is lifted out of flow and moved on Y only, clamped to the list's
 * visible box, with a drop slot opening at the landing position.
 */

/** Pointer travel before a press turns into a drag (below this it is a click). */
const DRAG_THRESHOLD_PX = 4;
/** Distance from the list edge that starts an auto-scroll, and its step. */
const EDGE_SCROLL_ZONE_PX = 18;
const EDGE_SCROLL_STEP_PX = 15;
/**
 * How much taller than the row the drop slot is, per side. The slot paints
 * nothing — it is plain empty space — and the lifted row is exactly row-sized
 * and sits on it, so without this band there would be no visible gap at all.
 */
const DROP_SLOT_PAD_PX = 5;

interface ConfigureTunnelProps {
  tunnels: IAhuBoardTunnel[];
  sections: IAhuBoardSection[];
}

/** Live drag, mirrored into state so the list can render the slot. */
interface DragState {
  id: number;
  fromIndex: number;
  /** Index the row will land on, in the list as it will read after the drop. */
  toIndex: number;
  /** `top` of the floating row inside the UL's content box. */
  top: number;
  height: number;
}

/** Row geometry captured on press, before the list is disturbed. */
interface DragGeometry {
  rows: { top: number; height: number }[];
  fromIndex: number;
  startY: number;
  id: number;
  /** The list's visible height at press. Read live it would grow by the slot's
   *  extra band and feed the clamp back into itself. */
  viewport: number;
}

const ConfigureTunnel = ({ tunnels, sections }: ConfigureTunnelProps) => {
  const dispatch = useAppDispatch();
  const [patchTunnel, { isLoading: airflowPending }] = usePatchAhuTunnelMutation();
  // `is_hybrid` gates the x_offset half of the airflow flip (legacy read it off
  // `fn['current.ahu']`); every tunnel here belongs to the same AHU.
  const { data: ahuUnit } = useGetAhuUnitQuery(tunnels[0]?.ahu_id ?? skipToken);
  const [moveSection] = useMoveAhuSectionMutation();
  const currentSectionId = useAppSelector((s) => s.ahuBoard.selection.currentSectionId);
  const [activeIndex, setActiveIndex] = useState(0);
  const [visibleStart, setVisibleStart] = useState(0);
  const [drag, setDrag] = useState<DragState | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const geometry = useRef<DragGeometry | null>(null);
  const dragResult = useRef<{ fromIndex: number; toIndex: number } | null>(null);
  /** Unbinds the in-flight gesture's document listeners; null when idle. */
  const detach = useRef<(() => void) | null>(null);
  /** A press that became a drag must not also fire the row's select-on-click. */
  const swallowClick = useRef(false);
  // Belt-and-braces against a double-click landing before `isLoading` renders.
  const airflowInFlight = useRef(false);

  // Follow external selection's tunnel (§8.2).
  useEffect(() => {
    if (currentSectionId == null) return;
    const section = sections.find((s) => s.id === currentSectionId);
    if (!section) return;
    const idx = tunnels.findIndex((t) => t.id === section.tunnel_id);
    if (idx !== -1) setActiveIndex(idx);
  }, [currentSectionId, sections, tunnels]);

  // ensureActiveTunnelIndexVisible (tunnels.section.js) — re-centers the
  // MAX_VISIBLE_TUNNEL_INDEXES-wide tab window on the active tunnel whenever
  // it changes, so the active tab is never scrolled out of view regardless
  // of how it became active (tab click, canvas/list selection, prev/next
  // nav, section create/delete).
  useEffect(() => {
    setVisibleStart(getVisibleTunnelIndexes(activeIndex, tunnels.length, MAX_VISIBLE_TUNNEL_INDEXES).start);
  }, [activeIndex, tunnels.length]);

  const activeTunnel = tunnels[Math.min(activeIndex, Math.max(0, tunnels.length - 1))];
  const tunnelSections = activeTunnel
    ? sections
        .filter((s) => s.tunnel_id === activeTunnel.id)
        .sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0))
    : [];

  /**
   * `model/tunnel.js:203-230` gated the toggle by putting `.non-clickable` on
   * every `.btn-tunnel-airflow-direction` for the life of the PATCH, so a
   * second click could not queue another save. The canvas arrow keeps that
   * (AhuCanvas.tsx `toggleAirflowDirection`); the toolbar's own button had
   * lost it, so repeated clicks fired one PATCH each. Re-enable on settle —
   * success *and* failure (legacy leaves it stuck on error).
   *
   * The body itself comes from the shared `airflowFlipPatch`, which carries
   * the re-based `x_offset` on a hybrid AHU — legacy kept that in the one
   * model method all three airflow affordances called, so all three keep it.
   */
  const toggleAirflow = (tunnel: IAhuBoardTunnel) => {
    if (airflowInFlight.current) return;
    airflowInFlight.current = true;
    patchTunnel({
      id: tunnel.id,
      ahuId: tunnel.ahu_id,
      data: airflowFlipPatch(tunnel, !!ahuUnit?.is_hybrid),
    })
      .unwrap()
      .then(() => {
        // Hybrid: same re-read the pill and the canvas arrow take — the flip
        // restates the unit's lug placement server-side
        // (refetchHybridLugPlacements).
        void dispatch(refetchHybridLugPlacements(tunnel.ahu_id, { hideWhilePending: true }));
        ahuBoardBus.emit('change:tunnel:dimensions', {
          tunnel_id: tunnel.id,
          render: true,
          accessories_fetch: false,
        });
        void dispatch(validateTunnel(tunnel.id));
      })
      .catch(() => undefined)
      .finally(() => {
        airflowInFlight.current = false;
      });
  };

  const commitMove = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || !activeTunnel) return;
    const ordered = tunnelSections.map((s) => s.id);
    const sourceId = ordered[fromIndex];
    if (sourceId == null || ordered[toIndex] == null) return;
    ordered.splice(fromIndex, 1);
    ordered.splice(toIndex, 0, sourceId);
    // `next_to` is an *order_rate*, not an index, and the API reads it against
    // the pre-move numbering (section_viewset.move): a downward move lands on
    // `next_to`, an upward one on `next_to + 1`. So the value is the current
    // order_rate of whichever row ends up directly above — `toIndex + 1` going
    // down, `toIndex` going up (0 = upstream edge). This is the same pair of
    // cases legacy computed from the swapped-past row (tunnels.section.js
    // `reorder_sections`: `order_rate < changed ? changed : changed - 1`).
    const nextTo = toIndex > fromIndex ? toIndex + 1 : toIndex;
    moveSection({ id: sourceId, next_to: nextTo })
      .unwrap()
      .then(() => {
        // Silent renumber from DOM order → collection.sort() (§8.2).
        dispatch(
          ahuBoardApi.util.updateQueryData(
            'getTunnelSectionList',
            activeTunnel.id,
            (rows) => {
              rows.forEach((row) => {
                const idx = ordered.indexOf(row.id);
                if (idx !== -1) row.order_rate = idx + 1;
              });
              rows.sort((a, b) => (a.order_rate ?? 0) - (b.order_rate ?? 0));
            }
          )
        );
        ahuBoardBus.emit('sections:reordered', activeTunnel.id);
        ahuBoardBus.emit('redraw:canvas');
        void dispatch(validateAll3D(tunnels.map((t) => t.id)));
      })
      .catch(() => undefined);
  };

  /**
   * Where the row would land, from its current top: the gap in the
   * source-removed list whose top is nearest the floating row's own top. Picked
   * by distance rather than by centres crossing so that the slot is always
   * drawn exactly where the row is being held — a crossing test is a hair's
   * breadth from the wrong answer on the boundary, and the rows are not quite
   * uniform (`li:last-child` drops its 1px rule).
   *
   * Measured against the geometry captured on press: the live DOM moves under
   * us as the slot opens, so reading it back would feed the result into its own
   * input.
   */
  const landingIndex = (g: DragGeometry, top: number) => {
    const others = g.rows.filter((_, i) => i !== g.fromIndex);
    let gapTop = g.rows[0].top;
    let best = 0;
    let bestDistance = Infinity;
    for (let k = 0; k <= others.length; k += 1) {
      const distance = Math.abs(gapTop - top);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = k;
      }
      if (k < others.length) gapTop += others[k].height;
    }
    return best;
  };

  const onRowPointerDown = (e: React.PointerEvent<HTMLLIElement>, fromIndex: number) => {
    const list = listRef.current;
    swallowClick.current = false;
    if (e.button !== 0 || fromIndex < 0 || !list || tunnelSections.length < 2 || geometry.current) {
      return;
    }

    const rows = Array.from(list.querySelectorAll<HTMLLIElement>('li.drag')).map((el) => ({
      top: el.offsetTop,
      height: el.offsetHeight,
    }));
    if (rows.length !== tunnelSections.length) return;

    geometry.current = {
      rows,
      fromIndex,
      startY: e.clientY,
      id: tunnelSections[fromIndex].id,
      viewport: list.clientHeight,
    };
    dragResult.current = null;
    let started = false;

    const onMove = (ev: PointerEvent) => {
      const g = geometry.current;
      if (!g || !listRef.current) return;
      const delta = ev.clientY - g.startY;
      if (!started) {
        if (Math.abs(delta) < DRAG_THRESHOLD_PX) return;
        started = true;
        swallowClick.current = true;
      }

      const ul = listRef.current;
      const box = ul.getBoundingClientRect();
      // Legacy stepped the parent's scrollTop rather than running a timer, so
      // the list only creeps while the pointer keeps moving. Same here.
      if (ev.clientY < box.top + EDGE_SCROLL_ZONE_PX) ul.scrollTop -= EDGE_SCROLL_STEP_PX;
      else if (ev.clientY > box.bottom - EDGE_SCROLL_ZONE_PX) ul.scrollTop += EDGE_SCROLL_STEP_PX;

      // Y only, and never past the list's visible box — the two things the
      // native drag ghost would not honour.
      const source = g.rows[g.fromIndex];
      const min = ul.scrollTop;
      const max = Math.max(min, min + g.viewport - source.height);
      const top = Math.min(Math.max(source.top + delta, min), max);

      const toIndex = landingIndex(g, top);
      dragResult.current = { fromIndex: g.fromIndex, toIndex };
      setDrag({ id: g.id, fromIndex: g.fromIndex, toIndex, top, height: source.height });
    };

    const finish = (commit: boolean) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
      detach.current = null;
      const result = dragResult.current;
      geometry.current = null;
      dragResult.current = null;
      setDrag(null);
      if (commit && result) commitMove(result.fromIndex, result.toIndex);
    };
    const onUp = () => finish(true);
    const onCancel = () => finish(false);

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    detach.current = () => finish(false);
  };

  // Closing the window mid-gesture would otherwise leave the document
  // listeners bound.
  useEffect(() => () => detach.current?.(), []);

  const dragged = drag ? tunnelSections.find((s) => s.id === drag.id) ?? null : null;
  const staticRows = dragged ? tunnelSections.filter((s) => s.id !== drag?.id) : tunnelSections;

  const renderRow = (section: IAhuBoardSection, floating: boolean) => (
    <li
      key={section.id}
      className={`clearfix drag${floating ? ' is-dragging' : ''}`}
      id="section-item"
      data-id={section.id}
      data-name={section.tunnel_id}
      // Offset by the slot's pad so a snapped row sits centred in its band.
      style={floating && drag ? { top: drag.top + DROP_SLOT_PAD_PX } : undefined}
      onPointerDown={
        floating
          ? undefined
          : (e) => onRowPointerDown(e, tunnelSections.findIndex((s) => s.id === section.id))
      }
      onMouseEnter={() =>
        ahuBoardBus.emit('hover:section', {
          section: { id: section.id },
          color: 0x9bb3d7,
        })
      }
      onMouseLeave={() => ahuBoardBus.emit('hover:section', { section: null })}
      onClick={() => {
        if (swallowClick.current) {
          swallowClick.current = false;
          return;
        }
        ahuBoardBus.emit('force:select:section', section.id);
        dispatch(selectSection({ sectionId: section.id }));
        ahuBoardBus.emit('select:section', { section: { id: section.id } });
      }}
    >
      <label>
        <span className="section-name">
          {String(section.name ?? '')}
          {section.tag ? ` : ${section.tag}` : ''}
        </span>
        <span className="number_output" data-format="decimal">
          {formatDecimal(section.length)}
        </span>
        <i className="icon-two-lines" />
      </label>
    </li>
  );

  const dropSlot = drag ? (
    <li
      className="section-drop-slot"
      style={{ height: drag.height + DROP_SLOT_PAD_PX * 2 }}
      aria-hidden
    />
  ) : null;

  const tunnelsTotal = tunnels.length;
  const tunnelsMaxStart = Math.max(0, tunnelsTotal - MAX_VISIBLE_TUNNEL_INDEXES);
  const tunnelsWindowStart = Math.max(0, Math.min(visibleStart, tunnelsMaxStart));
  const tunnelsWindowEnd = Math.min(tunnelsTotal, tunnelsWindowStart + MAX_VISIBLE_TUNNEL_INDEXES);
  const tunnelsShowNav = tunnelsTotal > MAX_VISIBLE_TUNNEL_INDEXES;
  const tunnelsCenterSlot = Math.floor(MAX_VISIBLE_TUNNEL_INDEXES / 2);
  // shiftVisibleTunnelIndexes (tunnels.section.js) — shifts the window
  // without touching which tunnel is active.
  const shiftTunnelsWindow = (direction: number) =>
    setVisibleStart(Math.max(0, Math.min(tunnelsMaxStart, tunnelsWindowStart + direction)));

  return (
    <>
      <div className="tunnels-header">
        <span className="pl-24">Tunnel </span>
        <div className="tunnels-indexes">
          <div className="tunnels-name-list">
            {tunnels.slice(tunnelsWindowStart, tunnelsWindowEnd).map((t, i) => {
              const idx = tunnelsWindowStart + i;
              const isActive = idx === activeIndex;
              const isCentered =
                tunnelsShowNav && isActive && idx - tunnelsWindowStart === tunnelsCenterSlot;
              return (
                <span
                  key={t.id}
                  data-index={idx}
                  data-id={t.id}
                  className={`tunnels-name tunnels-name-index${isActive ? ' active ' : ''}${
                    isCentered ? ' tunnel-index-centered' : ''
                  }`}
                  onClick={() => setActiveIndex(idx)}
                >
                  {t.index}
                </span>
              );
            })}
          </div>
          <div className={`tunnels-indexes-nav${tunnelsShowNav ? '' : ' hide'}`}>
            <label
              className={`icon-chevron-left tunnels-indexes-arrow tunnels-indexes-arrow-left ka-has-tooltip${
                tunnelsWindowStart <= 0 ? ' disabled' : ''
              }`}
              {...{ tooltip: 'Show Previous' }}
              onClick={() => tunnelsWindowStart > 0 && shiftTunnelsWindow(-1)}
            />
            <label
              className={`icon-chevron-right tunnels-indexes-arrow tunnels-indexes-arrow-right ka-has-tooltip${
                tunnelsWindowEnd >= tunnelsTotal ? ' disabled' : ''
              }`}
              {...{ tooltip: 'Show Next' }}
              onClick={() => tunnelsWindowEnd < tunnelsTotal && shiftTunnelsWindow(1)}
            />
          </div>
        </div>
        {tunnels.map((t, idx) => (
          <div
            key={t.id}
            className={`tunnel-list${idx === activeIndex ? ' active ' : ''}`}
            data-name={t.id}
          >
            <div className="directions-wrapper">
              <div className="hide">Section insertion direction</div>
              <div>
                <span className="airflow_direction" id={String(t.id)}>
                  Airflow Direction
                </span>
                <label
                  id={String(t.id)}
                  className={`icon-airflow-direction-icon btn-tunnel-airflow-direction ka-has-tooltip left-to-right left-to-right-${t.id}${
                    t.airflow_direction_id === 2 ? ' hide' : ''
                  }${airflowPending ? ' non-clickable' : ''}`}
                  aria-disabled={airflowPending}
                  {...{ tooltip: 'Reverse Direction' }}
                  onClick={() => toggleAirflow(t)}
                />
                <label
                  id={String(t.id)}
                  className={`icon-airflow-direction-icon btn-tunnel-airflow-direction ka-has-tooltip right-to-left right-to-left-${t.id}${
                    t.airflow_direction_id === 1 ? ' hide' : ''
                  }${airflowPending ? ' non-clickable' : ''}`}
                  aria-disabled={airflowPending}
                  {...{ tooltip: 'Reverse Direction' }}
                  onClick={() => toggleAirflow(t)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <span className="sections-header">
        <span> Section : Tag</span>
        <span>Length</span>
      </span>
      <ul ref={listRef} className={`sections-list-ul${drag ? ' is-reordering' : ''}`}>
        {/* First, not last: the floating row is out of flow so its position in
            the DOM is free, and keeping it off `li:last-child` stops it from
            dropping its 1px rule and changing height mid-drag. */}
        {dragged && renderRow(dragged, true)}
        {staticRows.map((section, i) => (
          <Fragment key={section.id}>
            {drag?.toIndex === i && dropSlot}
            {renderRow(section, false)}
          </Fragment>
        ))}
        {drag && drag.toIndex >= staticRows.length && dropSlot}
      </ul>
    </>
  );
};

export default ConfigureTunnel;
